/* Copyright (c) 2020-present CloudIO Inc */

import { baseComponentManager, ComponentManagerFn } from 'cloudio';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './FusionChartsIO';
import { AliasType } from '../../registry/Component.type';

const FusionChartsIOManager: ComponentManagerFn<'FusionChartsIO'> = (
  platform,
  type,
) => {
  return {
    ...baseComponentManager(platform, type),
    type,
    name: 'Fusion Charts',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      dsalias: {
        category: 'Data',
        label: 'DataSource Alias',
        required: true,
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
      keyAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Key Attribute',
        type: 'ComboAttributeChooser',
        helpText: 'It is the primary key field in the current data source.',
      },
      //TODO:
      // categoryAttributeLookup: {
      //   category: 'Data',
      //   label: 'Category Attribute Lookup',
      //   type: 'LookupCodeChooser',
      // },
      labelAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Label Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'The attribute that you would want to display as the label on the X-axis',
      },
      labelFormatExpression: {
        label: 'Label Format Expression',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Specify an expression that should be evaluated to a string.',
      },
      valueAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Value Attribute',
        required: true,
        type: 'ComboAttributeChooser',
        helpText:
          'It is a column through which you can get the data from DataSource.',
      },
      functionType: {
        category: 'Data',
        label: 'Value Function',
        options: toOptions(['Avg', 'Count', 'Max', 'Min', 'Sum']),
        required: true,
        type: 'select',
        helpText: 'Select a function to do arithmetic operations on data.',
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Data',
      },
      chartType: {
        label: 'Chart Type',
        options: async (item, { appUid, itemId, pageId, platform: p }) => {
          const parent = p.findParentOfType(
            appUid,
            pageId,
            itemId,
            'CombinationChartFC',
          );
          if (!parent) {
            return toOptions([
              'area2d',
              'bar2d',
              'bar3d',
              'column2d',
              'column3d',
              'doughnut2d',
              'doughnut3d',
              'line',
              'pie2d',
              'pie3d',
            ]);
          } else {
            return toOptions(['bar', 'area', 'line']);
          }
        },
        type: 'radio',
      },
      caption: {
        label: 'Caption',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Provide a title for the Chart.',
      },
      subCaption: {
        label: 'Sub-Caption',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Provide a sub title for the chart.',
      },
      defaultCenterLabel: {
        label: 'Default Center Label',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          'Provide an expression for the text to be displayed in the center. Use #{total} to insert the sum of all pie values.',
      },
      staggerLines: {
        label: 'Stagger Lines',
        type: 'number',
        helpText:
          'Enter a numeric value above 2 to control how many lines to stagger the label to. By default, all labels are displayed in a single line.',
      },
      labelStep: {
        label: 'Label Step',
        type: 'number',
        helpText:
          'Value to display every n-th label, instead of all labels on the x-axis.',
      },
      paletteColors: {
        label: 'Palette Colors',
        type: 'expression',
        helpText:
          'Specify an expression which evaluates to your custom list of hex colors for the data items. The list of colors have to be separated by comma e.g., "#FF0000, #0372AB, #FF5904...". The chart will cycle through the list of specified colors and then render the data plot accordingly.',
        returnType: async () => 'string',
      },
      darkBackGround: {
        label: 'Dark Background',
        type: 'boolean',
        helpText:
          'Enable this to display all the labels in lite color when the chart background color is dark.',
      },
      exportEnabled: {
        label: 'Export Enabled',
        type: 'boolean',
        helpText:
          'Enable this to allow export in png, jpg, pdf, svg or xls formats.',
      },
      yAxisMaxValue: {
        label: 'Y Axis Max Value',
        type: 'number',
        helpText: 'Value to set max value for y axis.',
      },
      enableSmartLabels: {
        label: 'Enable Smart Labels',
        type: 'boolean',
        helpText:
          'By default smart labels manage overlapping of labels even when a large number of labels are placed in the close vicinity. Uncheck to disable these smart labels.',
      },
      chartHeight: {
        label: 'Chart Height',
        type: 'number',
        max: 800,
        min: 0,
        step: 40,
        helpText:
          'Enter a number to specify the height of the chart. Set it to 1 to make the chart occupy the entire available height.',
      },
      chartWidth: {
        label: 'Chart Width',
        type: 'number',
        max: 1000,
        min: 0,
        step: 40,
        helpText:
          'Enter a number to specify the width of the chart. Set it to 1 to make the chart occupy the entire available width.',
      },
      placeValuesInside: {
        label: 'Place Values Inside',
        type: 'boolean',
        helpText:
          'Enable this to place values inside the symbols of the chart.',
      },
      showLabels: {
        label: 'Show Labels',
        type: 'boolean',
        helpText: 'Enable this to display labels of the chart.',
      },
      showLegend: {
        label: 'Show Legend',
        type: 'boolean',
        helpText: 'Enable this to display legend for the chart.',
      },
      legendPosition: {
        label: 'Legend Position',
        type: 'radio',
        options: toOptions(['Bottom', 'Right']),
      },
      showShadow: {
        label: 'Show Shadow',
        type: 'boolean',
        helpText: 'Enable this to display shadow for the chart.',
      },
      showValues: {
        label: 'Show Values',
        type: 'boolean',
        helpText:
          'Enable this to display values if any for the category fields.',
      },
      showPercentValues: {
        label: 'Show Percent Values',
        type: 'boolean',
        helpText:
          'Enable this to show percentage values in labels of the chart.',
      },
      lineDashed: {
        label: 'Dashed Lines',
        type: 'boolean',
        helpText: 'Enable this to show the lines as dashed line.',
      },
      showDivLines: {
        label: 'Show Div Lines',
        type: 'boolean',
        helpText: 'Enable this to display division lines.',
      },
      showCanvasBase: {
        label: 'Show Canvas Base',
        type: 'boolean',
        helpText: 'Enable this to display the Canvas Base.',
      },
      showXAxisValues: {
        label: 'Show X-Axis Values',
        type: 'boolean',
        helpText: 'Enable this to display values along X-axis.',
      },
      showYAxisValues: {
        label: 'Show Y-Axis Values',
        type: 'boolean',
        helpText: 'Enable this to display values along Y-axis.',
      },
      showXAxisLine: {
        label: 'Show X-Axis Line',
        type: 'boolean',
        helpText: 'Enable this to display a line along X-axis.',
      },
      showYAxisLine: {
        label: 'Show Y-Axis Line',
        type: 'boolean',
        helpText: 'Enable this to display a line along Y-axis.',
      },
      rotateValues: {
        label: 'Rotate Values',
        type: 'boolean',
        helpText: 'Enable this to rotate the data of the chart.',
      },
      use3DLighting: {
        label: 'Use 3D Lighting',
        type: 'boolean',
        helpText: 'Enable this to appear 3D lighting effect for the chart.',
      },
      canvasBaseColor: {
        label: 'Canvas Base Color',
        type: 'string',
        helpText: 'Provide a color in Hex for canvas base. eg.#FF00FF',
      },
      xAxisName: {
        label: 'X-Axis Name',
        type: 'string',
        helpText:
          'Provide a name that you would want to display on the X-axis.',
      },
      yAxisName: {
        label: 'Y-Axis Name',
        type: 'string',
        helpText:
          'Provide a name that you would want to display on the Y-axis.',
      },
      labelDisplay: {
        label: 'Label Display',
        type: 'radio',
        options: toOptions(['auto', 'wrap', 'stagger', 'rotate']),
      },
      slantLabels: {
        label: 'Slant Labels',
        type: 'boolean',
        helpText: 'Enable this to display labels in slant manner.',
      },
      plotToolText: {
        label: 'Plot ToolText',
        type: 'string',
        helpText:
          'You can either specify a constant string as the tooltip text or you can use variable values from the data level by prefixing a $ to the attribute name. Ex: $label : $dataValue',
      },
      pieRadius: {
        label: 'Pie Radius',
        type: 'string',
        helpText:
          'Specify the outer radius of the pie/doughnut chart using this property. You can specify the value in both percentage and pixel values.',
      },
      doughnutRadius: {
        label: 'Doughnut Radius',
        type: 'string',
        helpText:
          'Specify the inner radius of the doughnut chart using this property. You can specify the value in both percentage and pixel values.',
      },
      centerLabelFontSize: {
        label: 'Center Label Font-Size',
        type: 'number',
        helpText: 'Provide a number to set the size of the center label.',
      },
      labelFontSize: {
        label: 'Label Font-Size',
        type: 'number',
        helpText: 'Provide a number to set the size of the label.',
      },
      labelPosition: {
        label: 'Label Position',
        type: 'radio',
        options: toOptions(['inside', 'outside']),
      },
      valuePosition: {
        label: 'Value Position',
        type: 'radio',
        options: toOptions(['inside', 'outside']),
      },
      //TODO: Currently showing type error:
      // exportFileName: {
      //   property: 'Export File Name',
      //   type: 'expression',
      //   helpText:
      //     'Specify the name (excluding the extension) of the output (export) file. default: chart',
      //   returnType: async () => 'string',
      // },
      pieYScale: {
        label: 'Pie Y-axis Scale',
        type: 'number',
        helpText: 'Use this to adjust the slant of the Pie/Doughtnut chart.',
      },
      pieSliceDepth: {
        label: 'Pie Slice Depth',
        type: 'number',
        helpText: 'Use this to adjust the depth of the Pie/Doughtnut chart.',
      },
      centerOffsetX: {
        label: 'X-axis Center Offset',
        type: 'string',
        helpText:
          'Use this to adjust the position of the center label in 3D-Doughtnut chart w.r.t. the X-axis. ',
      },
      centerOffsetY: {
        label: 'Y-axis Center Offset',
        type: 'string',
        helpText:
          'Use this to adjust the Y-axis position of the center label in 3D-Doughtnut chart w.r.t. the Y-axis. Ex: 1, -2',
      },
      seriesName: {
        label: 'Series Name',
        type: 'string',
        helpText:
          'Lets you specify the series name for a particular dataset. For example, if you plot a chart to indicate monthly sales analysis for 2005 and 2006, the seriesName for the first dataset will be 2005 and that of the second will be 2006. The seriesName of a dataset is shown in the legend.',
      },
      parentYAxis: {
        label: 'Parent Y Axis',
        category: 'Data',
        options: toOptions(['primary', 'secondary']),
        required: true,
        type: 'select',
        helpText:
          'This attribute allows you to set the parent Y-axis of the data (primary or secondary). Primary data are drawn against the left y-axis, and the secondary data are drawn against the right y-axis.',
      },
      categoryKeyAttribute: {
        label: 'Category Key Attribute',
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        type: 'ComboAttributeChooser',
        helpText:
          'Select an attribute so that mathematical functions will be performed on this attribute.',
      },
      showIn2D: {
        label: 'Show in 2D',
        type: 'boolean',
        helpText:
          'Check this property to render a bar chart in 2D, when dual Y-axis is unchecked.',
        defaultValue: false,
      },
      showStacked: {
        label: 'Show Stacked',
        type: 'boolean',
        helpText: 'Check this property to show stacked chart.',
        defaultValue: false,
      },
      categoryAttribute: {
        label: 'Category Attribute',
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        type: 'ComboAttributeChooser',
        helpText:
          'To display all the categories based on the attribute that you provide in a legend at the bottom.',
      },
      orderByAttribute: {
        label: 'Order By Attribute',
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        type: 'ComboAttributeChooser',
        helpText:
          'To display all the categories based on the attribute that you provide in a legend at the bottom.',
      },
      showTrendlines: {
        label: 'Show Trend Lines',
        type: 'boolean',
        category: 'Trend Lines',
        helpText: 'Enable this to show trend lines on the chart.',
      },
      valueOnRight: {
        label: 'Show Value on Right',
        type: 'boolean',
        category: 'Trend Lines',
        helpText: 'Enable this to show label on the right side.',
      },
      dashedLine: {
        label: 'Dashed lines',
        type: 'boolean',
        category: 'Trend Lines',
        helpText: 'Enable this to show the trend lines as dashed line.',
      },
      lineToolText: {
        label: 'Line ToolText',
        type: 'string',
        category: 'Trend Lines',
        helpText:
          'You can specify the ToolText string to be displayed when hovering over the trend line.',
      },
      lineColor: {
        label: 'Line Color',
        type: 'string',
        category: 'Trend Lines',
        helpText:
          'Color of the line in hex e.g., "#FF0000, #0372AB, #FF5904...".',
      },
      displayValue: {
        label: 'Label text',
        type: 'string',
        category: 'Trend Lines',
        helpText:
          'Label of the trend line. If the label field is empty, it will take the value of the trend line.',
      },
      startValue: {
        label: 'Start Value',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Trend Lines',
        helpText: 'Provide a start value for the Trend line.',
      },
      endValue: {
        label: 'End Value',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Trend Lines',
        helpText:
          'Provide an end value for the trend line. If not specified, the trend line will extend as a straight line from the start value.',
      },
    },
    isPropertyVisible(
      prop,
      itemData,
      { appUid, itemId, pageId, platform: p },
    ): boolean {
      const chartType = itemData.chartType || '';
      const parent = p.findParentOfType(
        appUid,
        pageId,
        itemId,
        'CombinationChartFC',
      );
      if (parent) {
        switch (prop) {
          case 'exportEnabled':
          case 'caption':
          case 'subCaption':
          case 'placeValuesInside':
          case 'labelDisplay':
          case 'labelStep':
          case 'showXAxisLine':
          case 'showYAxisLine':
          case 'xAxisName':
          case 'yAxisName':
          case 'rotateValues':
          case 'use3DLighting':
          case 'showShadow':
          case 'showLabels':
          case 'showLegend':
          case 'darkBackGround':
          case 'chartHeight':
          case 'chartWidth':
          case 'labelFontSize':
          case 'yAxisMaxValue':
          case 'staggerLines':
          case 'slantLabels':
          case 'legendPosition':
          case 'showDivLines':
          case 'canvasBaseColor':
          case 'showCanvasBase':
          case 'showXAxisValues':
          case 'showYAxisValues':
          case 'dashedLine':
          case 'startValue':
          case 'endValue':
          case 'lineColor':
          case 'valueOnRight':
          case 'displayValue':
          case 'lineToolText':
          case 'showTrendlines':
            return false;
          default:
        }
      }
      switch (prop) {
        // case 'exportFileName':
        //   return itemData.exportEnabled;
        case 'staggerLines':
          return itemData.labelDisplay === 'stagger';
        case 'slantLabels':
          return (
            itemData.labelDisplay === 'rotate' &&
            chartType.indexOf('doughnut') === -1 &&
            chartType.indexOf('pie') === -1
          );
        case 'yAxisMaxValue':
        case 'labelDisplay':
        case 'labelStep':
        case 'placeValuesInside':
        case 'showXAxisLine':
        case 'showYAxisLine':
        case 'xAxisName':
        case 'yAxisName':
        case 'canvasBaseColor':
          return (
            itemData.showCanvasBase &&
            (chartType.indexOf('column3d') === 0 ||
              chartType.indexOf('bar3d') === 0)
          );
        case 'showCanvasBase':
          return (
            chartType.indexOf('column3d') === 0 ||
            chartType.indexOf('bar3d') === 0
          );
        case 'showDivLines':
          return (
            chartType.indexOf('doughnut') === -1 &&
            chartType.indexOf('pie') === -1
          );
        case 'showLegend':
        case 'enableSmartLabels':
        case 'showPercentValues':
          return (
            chartType.indexOf('doughnut') !== -1 ||
            chartType.indexOf('pie') !== -1
          );
        case 'legendPosition':
          return itemData.showLegend;
        case 'defaultCenterLabel':
          return chartType.indexOf('doughnut') !== -1;
        case 'valuePosition':
          return (
            (chartType.indexOf('doughnut2d') !== -1 ||
              chartType.indexOf('pie2d') !== -1) &&
            itemData.showValues
          );
        case 'labelPosition':
          return (
            (chartType.indexOf('doughnut2d') !== -1 ||
              chartType.indexOf('pie2d') !== -1) &&
            itemData.showLabels
          );
        case 'pieRadius':
          return (
            chartType.indexOf('doughnut') !== -1 ||
            chartType.indexOf('pie') !== -1
          );
        case 'pieYScale':
        case 'pieSliceDepth':
          return (
            chartType.indexOf('doughnut3d') !== -1 ||
            chartType.indexOf('pie3d') !== -1
          );
        case 'doughnutRadius':
        case 'centerLabelFontSize':
          return chartType.indexOf('doughnut') !== -1;
        case 'centerOffsetX':
        case 'centerOffsetY':
          return chartType.indexOf('doughnut3d') !== -1;
        case 'seriesName':
        case 'parentYAxis':
        case 'showIn2D':
        case 'showStacked':
        case 'categoryAttribute':
        case 'categoryKeyAttribute':
          return !!parent;
        case 'labelFormatExpression':
          return false;
        case 'dashedLine':
        case 'startValue':
        case 'endValue':
        case 'lineColor':
        case 'valueOnRight':
        case 'displayValue':
        case 'lineToolText':
          return (
            !(
              itemData.chartType.indexOf('doughnut3d') === 0 ||
              itemData.chartType.indexOf('doughnut2d') === 0 ||
              itemData.chartType.indexOf('pie2d') === 0 ||
              itemData.chartType.indexOf('pie3d') === 0
            ) && itemData.showTrendlines
          );
        case 'showTrendlines':
          return !(
            itemData.chartType.indexOf('doughnut3d') === 0 ||
            itemData.chartType.indexOf('doughnut2d') === 0 ||
            itemData.chartType.indexOf('pie2d') === 0 ||
            itemData.chartType.indexOf('pie3d') === 0
          );
        case 'lineDashed':
          return itemData.chartType === 'line';
        default:
          return true;
      }
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV'
        ? { aliasProp: 'dsalias', embeddedPageIdProp: 'embeddedPageId' } // TODO: Add embeddedPageId prop
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.dsalias === alias,
    category: 'Fusion Charts',
    canHaveChildren: () => true,
    appendOverlay: true,
    icon: (item) => {
      const { chartType } = item.props;
      return {
        iconName:
          chartType === 'bar2d' || chartType === 'bar3d'
            ? 'chart-bar'
            : chartType === 'pie2d' ||
              chartType === 'pie3d' ||
              chartType === 'doughnut2d' ||
              chartType === 'doughnut3d'
            ? 'chart-pie'
            : chartType === 'area2d' || chartType === 'area'
            ? 'chart-area'
            : chartType === 'line'
            ? 'chart-line'
            : 'chart-column',
      };
    },
    getParentItemTypes: () => ['FlexItem', 'DataStoreReference', 'Form'],
    description: '2D and 3D - Column, Bar, Pie, Donut, Line, Area Charts.',
  };
};

export default FusionChartsIOManager;
